import { z } from "zod";
import { IsoTimestampSchema, paginatedResponse, UuidSchema } from "./common.js";
/**
 * Relay Quote module — wire-format Zod schemas.
 *
 * Shared by `@simpliwork/portal-api` and `@simpliwork/portal-frontend`.
 * snake_case on the wire (matches relay's other module schemas — see
 * `leads.ts` / `content.ts`); camelCase only on the TS-side types.
 *
 * M-QUOTE.2 ships the first cluster: catalog (items/pricing-rules/service-
 * areas), playbooks, document metadata, draft approvals, and follow-up
 * sequences with their enrollment endpoint. Document chunking + RAG search
 * land in M-QUOTE.4 alongside the AI workflows port.
 */
// ── catalog ────────────────────────────────────────────────────────────────
export const QuoteCatalogItemResponseSchema = z.object({
    id: UuidSchema,
    tenant_id: UuidSchema,
    name: z.string(),
    description: z.string().nullable(),
    base_price_min: z.string().nullable(),
    base_price_max: z.string().nullable(),
    duration_minutes: z.number().int().nullable(),
    is_active: z.boolean(),
    created_at: IsoTimestampSchema,
    updated_at: IsoTimestampSchema,
});
export const QuoteCatalogItemListResponseSchema = paginatedResponse(QuoteCatalogItemResponseSchema);
export const CreateQuoteCatalogItemRequestSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    base_price_min: z
        .string()
        .regex(/^\d+(\.\d{1,2})?$/)
        .optional(),
    base_price_max: z
        .string()
        .regex(/^\d+(\.\d{1,2})?$/)
        .optional(),
    duration_minutes: z.number().int().min(0).optional(),
    is_active: z.boolean().default(true).optional(),
});
export const UpdateQuoteCatalogItemRequestSchema = CreateQuoteCatalogItemRequestSchema.partial();
// ── pricing rules ──────────────────────────────────────────────────────────
// Configurable multipliers / surcharges / minimum-charge rules that adjust
// generated quote pricing at draft time. `rule_type` is a free-form
// discriminator (e.g. "fixed_surcharge", "percentage_uplift",
// "minimum_charge"); `config` is the type-specific payload (jsonb on the
// wire). Higher `priority` wins when multiple rules match a single quote.
export const QuotePricingRuleResponseSchema = z.object({
    id: UuidSchema,
    tenant_id: UuidSchema,
    name: z.string(),
    rule_type: z.string(),
    config: z.record(z.unknown()),
    priority: z.number().int(),
    is_active: z.boolean(),
    created_at: IsoTimestampSchema,
    updated_at: IsoTimestampSchema,
});
export const QuotePricingRuleListResponseSchema = paginatedResponse(QuotePricingRuleResponseSchema);
export const CreateQuotePricingRuleRequestSchema = z.object({
    name: z.string().min(1).max(200),
    rule_type: z.string().min(1).max(64),
    config: z.record(z.unknown()).default({}),
    priority: z.number().int().min(0).default(0).optional(),
    is_active: z.boolean().default(true).optional(),
});
// ── service areas ──────────────────────────────────────────────────────────
// Geographic regions a tenant serves. Two coverage styles (either or both):
// `zip_codes[]` for explicit enumeration, `radius_miles` + center lat/lng
// for a circle around a point. The Quote draft pipeline can filter
// in-area vs out-of-area leads.
export const QuoteServiceAreaResponseSchema = z.object({
    id: UuidSchema,
    tenant_id: UuidSchema,
    name: z.string(),
    zip_codes: z.array(z.string()).nullable(),
    radius_miles: z.string().nullable(),
    center_lat: z.string().nullable(),
    center_lng: z.string().nullable(),
    created_at: IsoTimestampSchema,
    updated_at: IsoTimestampSchema,
});
export const QuoteServiceAreaListResponseSchema = paginatedResponse(QuoteServiceAreaResponseSchema);
export const CreateQuoteServiceAreaRequestSchema = z.object({
    name: z.string().min(1).max(200),
    zip_codes: z.array(z.string().regex(/^\d{5}(-\d{4})?$/, "ZIP must be 5 or 9 digits")).optional(),
    radius_miles: z
        .string()
        .regex(/^\d+(\.\d{1,2})?$/, "Numeric string with up to 2 decimals")
        .optional(),
    center_lat: z
        .string()
        .regex(/^-?\d{1,3}(\.\d+)?$/, "Numeric string")
        .optional(),
    center_lng: z
        .string()
        .regex(/^-?\d{1,3}(\.\d+)?$/, "Numeric string")
        .optional(),
});
// ── playbooks ──────────────────────────────────────────────────────────────
export const QuotePlaybookResponseSchema = z.object({
    id: UuidSchema,
    tenant_id: UuidSchema,
    vertical: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    prompts: z.record(z.string()),
    is_active: z.boolean(),
    created_at: IsoTimestampSchema,
    updated_at: IsoTimestampSchema,
});
export const QuotePlaybookListResponseSchema = paginatedResponse(QuotePlaybookResponseSchema);
export const CreateQuotePlaybookRequestSchema = z.object({
    vertical: z.string().min(1).max(64),
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    prompts: z.record(z.string()).default({}),
    is_active: z.boolean().default(true).optional(),
});
export const UpdateQuotePlaybookRequestSchema = CreateQuotePlaybookRequestSchema.partial();
// ── documents ──────────────────────────────────────────────────────────────
export const QuoteDocumentTypeSchema = z.enum(["quote", "invoice", "proposal", "email", "other"]);
export const QuoteDocumentStatusSchema = z.enum(["pending", "processing", "ready", "failed"]);
export const QuoteDocumentResponseSchema = z.object({
    id: UuidSchema,
    tenant_id: UuidSchema,
    file_name: z.string(),
    mime_type: z.string(),
    storage_key: z.string(),
    document_type: QuoteDocumentTypeSchema,
    status: QuoteDocumentStatusSchema,
    error_message: z.string().nullable(),
    file_size_bytes: z.number().int().nullable(),
    created_at: IsoTimestampSchema,
    updated_at: IsoTimestampSchema,
});
export const QuoteDocumentListResponseSchema = paginatedResponse(QuoteDocumentResponseSchema);
// One extracted text chunk from a document, after pdf-parse/mammoth + the
// chunking step in `quote-document-process`. Surfaced via
// `GET /api/v1/quote/documents/:id/chunks` for the detail-view UI;
// embeddings live in a separate table and are not returned by this endpoint.
export const QuoteDocumentChunkResponseSchema = z.object({
    id: UuidSchema,
    document_id: UuidSchema,
    chunk_index: z.number().int().nonnegative(),
    content: z.string(),
    created_at: IsoTimestampSchema,
});
export const QuoteDocumentChunkListResponseSchema = paginatedResponse(QuoteDocumentChunkResponseSchema);
// ── drafts (operator approval queue) ──────────────────────────────────────
export const QuoteDraftStatusSchema = z.enum([
    "draft",
    "pending_approval",
    "approved",
    "rejected",
    "sent",
    "failed",
]);
export const QuoteDraftResponseSchema = z.object({
    id: UuidSchema,
    tenant_id: UuidSchema,
    lead_id: UuidSchema,
    ai_run_id: UuidSchema.nullable(),
    channel: z.enum(["email", "sms"]),
    subject: z.string(),
    body: z.string(),
    to_email: z.string(),
    from_email: z.string(),
    status: QuoteDraftStatusSchema,
    approved_at: IsoTimestampSchema.nullable(),
    sent_at: IsoTimestampSchema.nullable(),
    provider_message_id: z.string().nullable(),
    rejection_reason: z.string().nullable(),
    error: z.string().nullable(),
    created_at: IsoTimestampSchema,
    updated_at: IsoTimestampSchema,
});
export const QuoteDraftListResponseSchema = paginatedResponse(QuoteDraftResponseSchema);
export const ApproveQuoteDraftRequestSchema = z.object({});
export const RejectQuoteDraftRequestSchema = z.object({
    rejection_reason: z.string().max(2000).optional(),
});
// ── follow-up sequences ────────────────────────────────────────────────────
export const QuoteFollowupSequenceResponseSchema = z.object({
    id: UuidSchema,
    tenant_id: UuidSchema,
    name: z.string(),
    description: z.string().nullable(),
    is_active: z.boolean(),
    created_at: IsoTimestampSchema,
    updated_at: IsoTimestampSchema,
});
export const QuoteFollowupSequenceListResponseSchema = paginatedResponse(QuoteFollowupSequenceResponseSchema);
// Single step inside a sequence. The orchestrator's `quote-followup-sequence`
// Inngest function walks these in `step_order` ascending order, waiting
// `delay_hours` between each, then producing a draft from `subject_template`
// / `body_template` for operator approval.
export const QuoteFollowupSequenceStepResponseSchema = z.object({
    id: UuidSchema,
    sequence_id: UuidSchema,
    step_order: z.number().int().nonnegative(),
    delay_hours: z.number().int().nonnegative(),
    subject_template: z.string(),
    body_template: z.string(),
    created_at: IsoTimestampSchema,
});
// Single-sequence GET response — sequence metadata plus its full step list.
// Separate from the list response so the lighter listing endpoint stays cheap.
export const QuoteFollowupSequenceDetailResponseSchema = QuoteFollowupSequenceResponseSchema.extend({
    steps: z.array(QuoteFollowupSequenceStepResponseSchema),
});
export const CreateQuoteFollowupSequenceRequestSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    is_active: z.boolean().default(true).optional(),
    steps: z
        .array(z.object({
        step_order: z.number().int().min(0),
        delay_hours: z.number().int().min(0).default(24),
        subject_template: z.string().min(1).max(500),
        body_template: z.string().min(1).max(10000),
    }))
        .min(1)
        .max(20),
});
export const EnrollQuoteFollowupRequestSchema = z.object({
    lead_id: UuidSchema,
    sequence_id: UuidSchema,
});
export const QuoteFollowupEnrollmentResponseSchema = z.object({
    id: UuidSchema,
    tenant_id: UuidSchema,
    lead_id: UuidSchema,
    sequence_id: UuidSchema,
    status: z.enum(["active", "paused", "completed", "cancelled"]),
    current_step_order: z.number().int(),
    inngest_run_id: z.string().nullable(),
    created_at: IsoTimestampSchema,
    updated_at: IsoTimestampSchema,
});
//# sourceMappingURL=quote.js.map